import React from 'react';

// Utility functions
import { cartApi } from '~js/utils/cart-api';
import { getDisplayPrice } from '~js/utils/utils';
import { renderIcon } from '~js/utils/react-utils';

const LineItem = ({ item, loading, setLoading, removeText }) => {
  const grams = item.grams || 0;
  const kg = (grams / 1000).toFixed(1);
  const lb = Math.round(grams / 453.592);

  const maxLimit = item.maxLimit || 0;
  const limitMessage = item.limitMessage || '';
  const isAtLimit = maxLimit > 0 && item.quantity >= maxLimit;
  const exceedsLimit = maxLimit > 0 && item.quantity > maxLimit;
  const maxQty = item.inventory_quantity ?? 9999;
  const getProductUrlWithZ = (baseUrl) => {
    const currentUrl = new URL(window.location.href);
    const zParam = currentUrl.searchParams.get('z');
    if (zParam) {
      const productUrl = new URL(baseUrl, window.location.origin);
      productUrl.searchParams.set('z', zParam);
      return productUrl.toString();
    }
    return baseUrl;
  };

  const decreaseDisabled =
    loading?.status === true && loading?.itemKey === item.key;

  const increaseDisabled =
    isAtLimit ||
    item.quantity >= maxQty ||
    (loading?.status === true && loading?.itemKey === item.key);

  return (
    <div className="flex flex-col gap-3 border-b border-gray-100 pb-6">
      <div className="flex gap-3">
        {/* Item Image and Remove action */}
        <div className="w-20 flex-shrink-0">
          {item.image && (
            <a href={getProductUrlWithZ(item.url)}>
              <img
                src={item.image}
                alt={item.title}
                className="h-20 w-full rounded object-cover"
              />
            </a>
          )}

          <button
            onClick={() => cartApi.updateQty(item.key, 0, 'remove', setLoading)}
            className="mt-3 flex h-4 w-full justify-center text-xs font-book text-blue-950 underline"
            disabled={loading?.status === true && loading?.itemKey !== item.key}
          >
            {loading?.itemKey === item.key && loading?.action === 'remove'
              ? renderIcon('loader')
              : removeText}
          </button>
        </div>

        <div className="flex-1">
          {/* Item details & Price */}
          <div className="flex h-full justify-between gap-4">
            {/* Item details */}
            <div className="flex flex-1 flex-col justify-between">
              <div className="flex flex-col gap-1 text-blue-950">
                <h3 className="text-base font-medium">
                  <a href={getProductUrlWithZ(item.url)}>
                    {item.product_title} -{' '}
                    {item.variant_title
                      .replace(/\s*\(.*?\)/g, '')
                      .split(' / ')
                      .join(' | ')}
                  </a>
                </h3>
                {item.grams > 0 && (
                  <p className="text-sm font-book">
                    Net Wt. {lb} lb ({kg} kg)
                  </p>
                )}
              </div>
              <div className="flex w-fit items-center gap-3 border border-gray-100 px-2 py-0.5 text-sm text-blue-950">
                {/* Decrease button */}
                <button
                  onClick={() => {
                    if (!decreaseDisabled) {
                      cartApi.updateQty(
                        item.key,
                        item.quantity - 1,
                        'decrease',
                        setLoading,
                      );
                    }
                  }}
                  className={`w-3 ${
                    decreaseDisabled ? 'cursor-not-allowed' : ''
                  }`}
                  disabled={decreaseDisabled}
                >
                  {loading?.itemKey === item.key &&
                  loading?.action === 'decrease'
                    ? renderIcon('loader')
                    : renderIcon('minus')}
                </button>

                <div className="cursor-default text-center text-sm">
                  {item.quantity}
                </div>

                {/* Increase button */}
                <button
                  onClick={() => {
                    if (!increaseDisabled) {
                      cartApi.updateQty(
                        item.key,
                        item.quantity + 1,
                        'increase',
                        setLoading,
                      );
                    }
                  }}
                  className={`w-3 ${
                    increaseDisabled ? 'cursor-not-allowed' : ''
                  }`}
                  disabled={increaseDisabled}
                >
                  {loading?.itemKey === item.key &&
                  loading?.action === 'increase'
                    ? renderIcon('loader')
                    : renderIcon('plus')}
                </button>
              </div>
            </div>

            {/* Item Price */}
            <div className="text-right">
              <p
                className={`text-sm font-medium ${
                  item.selling_plan_allocation?.compare_at_price >
                  item.final_price
                    ? 'text-red-500'
                    : 'text-blue-950'
                }`}
              >
                {getDisplayPrice(item.final_price)}
              </p>
              {item.selling_plan_allocation?.compare_at_price >
                item.final_price && (
                <p className="text-p-friarGray text-xs line-through lg:text-sm">
                  {getDisplayPrice(
                    item.selling_plan_allocation.compare_at_price,
                  )}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Limit Message */}
      {isAtLimit && limitMessage && (
        <div className="flex items-center justify-center gap-2 rounded-lg bg-red-50 px-4 py-2 text-sm">
          <div className="h-4 w-4">{renderIcon('max-limit')}</div>
          <span className="text-xs font-book text-red-800">{limitMessage}</span>
        </div>
      )}
    </div>
  );
};

export default LineItem;
